import React, { useId, useMemo } from 'react';
import { Area, AreaChart, ReferenceArea, ReferenceDot, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { InspoStory } from '../../../services';

type GrowthChartProps = {
    story: InspoStory;
    /** Annotated with dollars, setup shading and the best month for the hero; a bare sparkline for the teaser. */
    size?: 'hero' | 'spark';
};

const EMERALD = '#0063d0'; // Royal Azure (name kept for the existing references)
const CORAL = '#ff6351';
const TOOLTIP_STYLE = { borderRadius: 8, fontSize: 12, background: 'rgb(var(--surface-raised))', border: '1px solid rgba(120, 113, 108, 0.35)' };

// #region dollars
const dollars = (n: number) => (n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${Math.round(n)}`);

/** [above this peak, use this step] — clean axis steps so ticks read $0, $10k, $20k… */
const STEPS: [number, number][] = [[40_000, 20_000], [15_000, 10_000], [0, 5_000]];

// #region niceTicks
const niceTicks = (max: number): number[] => {
    const step = (STEPS.find(([above]) => max > above) ?? STEPS[STEPS.length - 1])[1];
    const top = Math.max(step, Math.ceil(max / step) * step);
    return Array.from({ length: top / step + 1 }, (_, i) => i * step);
};

// #region GrowthChart
/**
 * Monthly customer sales. Featured brands show real dollars (rounded to $1k by the
 * server); anonymous brands show only the shape. Months before Wallmates finished
 * setting up ("Wallmates in beta") are shaded, because not every order ran through us yet.
 */
export const GrowthChart: React.FunctionComponent<GrowthChartProps> = ({ story, size = 'hero' }: GrowthChartProps): JSX.Element => {
    const gradientId = useId().replace(/:/g, '');
    const money = story.series.money;
    const data = useMemo(
        () => story.series.labels.map((label, i) => ({ label, value: money ? money[i] ?? 0 : story.series.index[i] ?? 0 })),
        [story.series, money]
    );
    const isHero = size === 'hero';
    const setupEnd = story.series.setup_until;
    const best = data.reduce((acc, d, i) => (d.value > (data[acc]?.value ?? -1) ? i : acc), 0);
    // Near the right edge the label would run off the chart, so it ends at the dot instead of centring on it.
    const nearEnd = best > data.length * 0.8;
    const ticks = money ? niceTicks(Math.max(0, ...data.map((d) => d.value))) : undefined;

    return (
        <div className="text-gray-400 dark:text-stone-500" style={{ height: isHero ? 260 : 40 }}>
            <ResponsiveContainer>
                <AreaChart data={data} margin={isHero ? { top: 30, right: 12, bottom: 0, left: 0 } : { top: 4, right: 2, bottom: 0, left: 2 }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={EMERALD} stopOpacity={0.3} />
                            <stop offset="100%" stopColor={EMERALD} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    {isHero && <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'currentColor' }} tickLine={false} axisLine={false} minTickGap={24} />}
                    <YAxis hide={!isHero || !money} width={44} tickFormatter={dollars} domain={[0, ticks?.[ticks.length - 1] ?? 'auto']} ticks={ticks} tick={{ fontSize: 11, fill: 'currentColor' }} tickLine={false} axisLine={false} />
                    {isHero && setupEnd > 0 && (
                        <ReferenceArea
                            x1={data[0]?.label}
                            x2={data[Math.min(setupEnd, data.length - 1)]?.label}
                            fill="currentColor"
                            fillOpacity={0.12}
                            label={{ value: 'Wallmates in beta', position: 'insideTopLeft', fontSize: 11, fill: 'currentColor' }}
                        />
                    )}
                    {isHero && (
                        <Tooltip
                            cursor={{ stroke: 'currentColor', strokeDasharray: '3 3' }}
                            formatter={(v) => [money ? dollars(Number(v)) : `${Number(v)}% of best month`, 'Customer sales']}
                            contentStyle={TOOLTIP_STYLE}
                            labelStyle={{ color: 'inherit' }}
                            itemStyle={{ color: EMERALD }}
                        />
                    )}
                    <Area type="monotone" dataKey="value" stroke={EMERALD} strokeWidth={isHero ? 2.5 : 2} fill={`url(#${gradientId})`} isAnimationActive={false} dot={false} />
                    {isHero && data[best] && (
                        <ReferenceDot
                            x={data[best].label}
                            y={data[best].value}
                            r={6}
                            fill={CORAL}
                            stroke="white"
                            strokeWidth={2}
                            ifOverflow="visible"
                            label={{ value: money ? `Best month ${story.figures.best_month}` : 'Best month', position: 'top', textAnchor: nearEnd ? 'end' : 'middle', dx: nearEnd ? 6 : 0, fontSize: 12, fontWeight: 600, fill: CORAL }}
                        />
                    )}
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};
